import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    bell: ['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    plus: ['M12 5v14', 'M5 12h14'],
    save: ['M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z', 'M17 21v-8H7v8', 'M7 3v5h8']
};

export default function ManageTripNotifications({ user, operatorDetails }) {
    const operatorId = operatorDetails?._id || user?.operatorId || (user?.userType === 'operator' ? user?._id : null);
    
    const [milestones, setMilestones] = useState([25, 15, 10, 5, 2]);
    const [operatorAlert, setOperatorAlert] = useState(2);
    
    const [newMilestone, setNewMilestone] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (operatorId) {
            fetchSettings();
        }
    }, [operatorId]);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/operators/${operatorId}`);
            if (res.data && res.data.tripNotificationSettings) {
                setMilestones(res.data.tripNotificationSettings.milestones || [25, 15, 10, 5, 2]);
                setOperatorAlert(res.data.tripNotificationSettings.operatorAlertMilestone || 2);
            }
        } catch (err) {
            console.error('Failed to fetch settings', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!operatorId) return;
        setSaving(true);
        setMessage('');
        try {
            await axios.put(`/api/operators/${operatorId}/trip-notifications`, {
                milestones,
                operatorAlertMilestone: operatorAlert
            });
            setMessage('Settings saved successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            console.error('Failed to save settings', err);
            setMessage('Failed to save settings.');
        } finally {
            setSaving(false);
        }
    };

    const handleAddMilestone = () => {
        const val = parseInt(newMilestone);
        if (!isNaN(val) && val > 0 && !milestones.includes(val)) {
            const updated = [...milestones, val].sort((a, b) => b - a);
            setMilestones(updated);
            setNewMilestone('');
        }
    };

    const handleRemoveMilestone = (val) => {
        const updated = milestones.filter(m => m !== val);
        setMilestones(updated);
        if (operatorAlert === val) {
            setOperatorAlert(updated.length > 0 ? updated[updated.length - 1] : 0);
        }
    };

    if (!operatorId) {
        return <div style={{ padding: 24 }}>Operator ID not found.</div>;
    }

    return (
        <div className="operator-page">
            <div className="op-breadcrumb">
                Trip Operations <span className="sep">/</span> <span className="current">Manage Notifications</span>
            </div>

            <div className="md-panel" style={{ background: '#fff', borderRadius: 16, padding: 32, border: '1px solid #e2e8f0', maxWidth: 800 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: '#f0f4ff', color: '#3f51b5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon d={icons.bell} size={24} />
                    </div>
                    <div>
                        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>Trip Notifications</h2>
                        <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: 14 }}>Configure minutes before a trip when drivers receive reminders.</p>
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading settings...</div>
                ) : (
                    <>
                        <div style={{ marginBottom: 32 }}>
                            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 12 }}>
                                Reminder Intervals (Minutes before trip)
                            </label>
                            
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                                {milestones.map(m => (
                                    <div key={m} style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', gap: 8 }}>
                                        <span style={{ fontWeight: 600, color: '#3f51b5' }}>{m} min</span>
                                        <button 
                                            onClick={() => handleRemoveMilestone(m)}
                                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2, display: 'flex' }}
                                            title="Remove"
                                        >
                                            <Icon d={icons.trash} size={14} />
                                        </button>
                                    </div>
                                ))}
                                {milestones.length === 0 && <span style={{ color: '#94a3b8', fontSize: 14, fontStyle: 'italic' }}>No reminders configured.</span>}
                            </div>

                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <input 
                                    type="number"
                                    min="1"
                                    value={newMilestone}
                                    onChange={e => setNewMilestone(e.target.value)}
                                    placeholder="e.g. 30"
                                    className="md-input border-premium"
                                    style={{ width: 120 }}
                                />
                                <button 
                                    onClick={handleAddMilestone}
                                    className="md-save-btn btn-premium"
                                    style={{ padding: '8px 16px', background: '#1e293b' }}
                                    disabled={!newMilestone}
                                >
                                    <Icon d={icons.plus} size={16} /> Add Minute
                                </button>
                            </div>
                        </div>

                        <div style={{ marginBottom: 32, padding: 20, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
                            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#991b1b', marginBottom: 8 }}>
                                Operator Alert Threshold
                            </label>
                            <p style={{ fontSize: 13, color: '#b91c1c', margin: '0 0 12px 0' }}>
                                Select the minute milestone where YOU (the operator) should receive an alert if the driver has NOT yet started the trip.
                            </p>
                            <select 
                                className="md-input border-premium" 
                                style={{ maxWidth: 200, borderColor: '#fca5a5' }}
                                value={operatorAlert}
                                onChange={e => setOperatorAlert(parseInt(e.target.value))}
                            >
                                {milestones.length > 0 ? milestones.map(m => (
                                    <option key={m} value={m}>{m} minutes before</option>
                                )) : (
                                    <option value="0">None</option>
                                )}
                            </select>
                        </div>

                        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
                            <button 
                                onClick={handleSave}
                                disabled={saving}
                                className="md-save-btn btn-premium"
                                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', fontSize: 15 }}
                            >
                                <Icon d={icons.save} size={18} /> {saving ? 'Saving...' : 'Save Settings'}
                            </button>
                            {message && (
                                <span style={{ color: message.includes('success') ? '#16a34a' : '#dc2626', fontWeight: 500, fontSize: 14 }}>
                                    {message}
                                </span>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
